import { QuestionIcon } from '@phosphor-icons/react'
import { ReactElement, useState } from 'react'
import ReadOnlyField from 'app/components/ReadOnlyField'
import { SectionCard } from 'app/components/SectionCard'
import { Label } from 'app/components/ui/label'
import { Switch } from 'app/components/ui/switch'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { useAIAgentStore, useLocationStore } from 'app/stores/StoreContext'
import { AgentBasics, basicsUpdate } from 'app/lib/aiAgentOverview'
import { AiAgentBasicsFields } from './AiAgentBasicsFields'
import { AiAgentScriptingSection } from './AiAgentScriptingSection'
import { AgentStatusBadge } from './AiAgentStatusCells'
import { useUser } from 'app/utils'
import { AiAgentBusinessHours } from './AiAgentBusinessHours'
import { AiAgentOffersServices } from './AiAgentOffersServices'
import { useSettingsSection } from './useSettingsSave'
import type { AiAgentOverview } from './useAiAgentOverview'

const TRANSFER_HINT = `Calls the agent can't handle go to this number, so it is critical to how callers reach your office. To change it, contact Trackable support.`

function Skeleton({ title }: { title: string }): ReactElement {
  return (
    <SectionCard attached title={title}>
      <div className="flex flex-col gap-3" aria-busy="true">
        <span className="block h-4 w-2/3 animate-pulse rounded bg-surface-muted" />
        <span className="block h-4 w-1/2 animate-pulse rounded bg-surface-muted" />
        <span className="block h-4 w-3/5 animate-pulse rounded bg-surface-muted" />
      </div>
    </SectionCard>
  )
}

/**
 * The read-only "what your agent is and knows" sections of AI Agent Settings
 * (TRK-6100), in the house settings recipes: label-over-value fields in a card,
 * SectionTable for offers and services.
 * Fields edit in place; the page's save bar saves every section at once.
 */
export function AiAgentOverviewSections({
  locationId,
  overview,
}: {
  locationId: string
  overview: AiAgentOverview
}): ReactElement {
  const [draft, setDraft] = useState<AgentBasics | null>(null)
  const isSuper = useUser()?.type === `super`
  const agentStore = useAIAgentStore()
  const changes = draft ? basicsUpdate(draft, overview.basics) : {}
  useSettingsSection(`basics`, {
    dirty: Object.keys(changes).length > 0,
    save: () => agentStore.updateConfig(locationId, changes),
    discard: () => setDraft(null),
  })

  const locationStore = useLocationStore()
  const [liveDraft, setLiveDraft] = useState<boolean | null>(null)
  const live = liveDraft ?? overview.status.voiceLive
  useSettingsSection(`voiceLive`, {
    dirty: liveDraft != null && liveDraft !== overview.status.voiceLive,
    discard: () => setLiveDraft(null),
    save: async () => {
      const result = await locationStore.update(locationId, { isAIAgentLive: live })
      if (result.error) throw result.error
    },
  })

  if (overview.loading) {
    return (
      <>
        <Skeleton title="Details" />
        <Skeleton title="Business Hours" />
      </>
    )
  }
  if (overview.unavailable) {
    return (
      <SectionCard title="Details" attached>
        <p className="text-body-sm text-subtle">
          No agent is set up for this location yet, or it could not be loaded. Contact support if you expected one.
        </p>
      </SectionCard>
    )
  }

  const { agent } = overview

  return (
    <>
      <SectionCard
        title="Details"
        attached
        headerButtons={
          isSuper ? (
            <span className="flex items-center gap-2">
              <Label htmlFor="voice-agent-live" className="text-body-sm">
                Voice agent {live ? `on` : `off`}
              </Label>
              <Switch id="voice-agent-live" checked={live} onCheckedChange={setLiveDraft} />
            </span>
          ) : (
            <AgentStatusBadge tone={live ? `online` : `redirecting`} />
          )
        }
      >
        <div className="grid gap-x-8 gap-y-5 md:grid-cols-2">
          <AiAgentBasicsFields draft={draft ?? overview.basics} onChange={setDraft} locationName={agent.practiceName} />
          {!isSuper && (
            <ReadOnlyField
              label="Transfers calls to"
              value={agent.transferPhone}
              adornment={
                <TooltipWrapper message={TRANSFER_HINT} placement="bottom-start" tooltipClassName="max-w-xs">
                  <span className="flex cursor-help items-center text-subtle" aria-label="About call transfers">
                    <QuestionIcon className="h-4 w-4" />
                  </span>
                </TooltipWrapper>
              }
            />
          )}
        </div>
      </SectionCard>

      <AiAgentBusinessHours
        locationId={locationId}
        hours={agent.hours}
        openHours={agent.openHours}
        fromSchedule={agent.hoursSource === `schedule`}
        voiceAgentTools={agent.voiceAgentTools}
        timezone={agent.timezone}
      />

      <AiAgentScriptingSection
        locationId={locationId}
        greeting={overview.basics.greeting}
        agentName={overview.basics.agentName}
        businessName={agent.businessName ?? agent.practiceName}
        postBookingMessage={agent.postBookingMessage ?? ``}
        hygienePostBookingMessage={agent.hygienePostBookingMessage ?? ``}
        confirmationOn={agent.smsConfirmationEnabled}
        practiceName={agent.practiceName}
        officePhone={agent.mainPhone}
        phraseRules={agent.phraseRules}
      />

      <AiAgentOffersServices locationId={locationId} editable={overview.editable} />
    </>
  )
}
